import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaClock } from "react-icons/fa";
import iqQuestions from "../data/iqQuestions";
import getApiBaseUrl from "../utils/api";
import { isSessionActive } from "../utils/auth";

function IqQuizPage() {
    const navigate = useNavigate();

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(20 * 60); // 20 minutes
    const [submitted, setSubmitted] = useState(false);

    const API_URL = getApiBaseUrl();
    // ✅ TIMER
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // ✅ AUTO SUBMIT
    useEffect(() => {
        if (timeLeft <= 0) {
            handleSubmit();
        }
    }, [timeLeft]);

    // ⏱ FORMAT TIME
    const formatTime = (seconds) => {
        if (seconds <= 0) return "0:00";
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
    };

    // 🟢 SELECT OPTION
    const handleOptionSelect = (option) => {
        setAnswers((prev) => ({
            ...prev,
            [currentQuestionIndex]: option,
        }));
    };

    // ➡ NEXT
    const handleNext = () => {
        if (currentQuestionIndex < iqQuestions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
        } else {
            handleSubmit();
        }
    };

    // ⬅ PREVIOUS
    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((prev) => prev - 1);
        }
    };

    // ─── RAW SCORE → IQ NUMBER (based on official scoring table) ───────────
    // Raw Score (out of 30) → Percentile → IQ range
    //  29–30 → Top 0.5%   → 135+
    //  26–28 → Top 2%     → 130–134
    //  23–25 → Top 9%     → 120–129
    //  20–22 → Top 25%    → 110–119
    //  15–19 → Middle 50% → 90–109
    //  11–14 → Bottom 25% → 80–89
    //   8–10 → Bottom 9%  → 70–79
    //   0–7  → Bottom 2%  → below 70
    //
    // Bands are defined for a 30-question test. If the quiz ever has a
    // different question count, the raw score is proportionally scaled to
    // a /30 equivalent before lookup, so the table still applies correctly.
    //
    // NOTE: this table only maps raw score -> a specific IQ number (via
    // linear interpolation within the band). The human-readable
    // classification label shown to the user is derived SEPARATELY from
    // the final IQ number below (getIQClassification), so there is a
    // single source of truth for labels and it can be updated without
    // touching the raw-score math.
    const IQ_BANDS = [
        { rawMin: 29, rawMax: 30, iqMin: 135, iqMax: 145 },
        { rawMin: 26, rawMax: 28, iqMin: 130, iqMax: 134 },
        { rawMin: 23, rawMax: 25, iqMin: 120, iqMax: 129 },
        { rawMin: 20, rawMax: 22, iqMin: 110, iqMax: 119 },
        { rawMin: 15, rawMax: 19, iqMin: 90,  iqMax: 109 },
        { rawMin: 11, rawMax: 14, iqMin: 80,  iqMax: 89  },
        { rawMin: 8,  rawMax: 10, iqMin: 70,  iqMax: 79  },
        { rawMin: 0,  rawMax: 7,  iqMin: 55,  iqMax: 69  },
    ];

    // ─── IQ NUMBER → CLASSIFICATION LABEL ───────────────────────────────────
    // Single source of truth for the human-readable label. Keep this in
    // sync with the identical function in AdminDashboard.jsx.
    const getIQClassification = (iq) =>
        iq >= 130 ? "Very Superior" :
        iq >= 120 ? "Superior" :
        iq >= 110 ? "Above Average" :
        iq >= 90  ? "Average" :
        iq >= 80  ? "Below Average" :
        iq >= 70  ? "Borderline" :
                    "Estimated Extremely Low";

    const calculateIQ = (rawScore, total) => {
        if (!total || total <= 0) return { iq: 100, classification: getIQClassification(100) };

        // Scale to a /30 equivalent so the table (built for 30 questions) still applies
        const scaledScore = total === 30 ? rawScore : (rawScore / total) * 30;

        const band =
            IQ_BANDS.find((b) => scaledScore >= b.rawMin && scaledScore <= b.rawMax) ||
            IQ_BANDS[IQ_BANDS.length - 1];

        // Linear interpolation within the matched band for a smooth, specific score
        // rather than a flat value across the whole range.
        const rawRange = band.rawMax - band.rawMin;
        const iqRange = band.iqMax - band.iqMin;
        const fraction = rawRange === 0 ? 1 : (scaledScore - band.rawMin) / rawRange;
        const iq = Math.round(band.iqMin + fraction * iqRange);

        return { iq, classification: getIQClassification(iq) };
    };

    // 💾 SAVE RESULT
    const saveTestResult = async (rawScore, total, iq, classification) => {
        const token = localStorage.getItem("token");
        if (!isSessionActive()) return;

        try {
            const res = await fetch(`${API_URL}/tests/save`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                credentials: "include",
                body: JSON.stringify({
                    testName: "IQ Test",
                    result: {
                        score: rawScore,
                        total: total,
                        iq: iq,                       // ✅ same value shown on the results page
                        classification: classification // ✅ e.g. "Above Average", "Superior"
                    },
                }),
            });

            const data = await res.json();
            console.log("SAVE RESPONSE:", data);

        } catch (err) {
            console.error("Failed to save IQ result", err);
        }
    };

    // ✅ SUBMIT
    const handleSubmit = () => {
        if (submitted) return;
        setSubmitted(true);

        let correctCount = 0;

        iqQuestions.forEach((q, index) => {
            if (answers[index] === q.answer) {
                correctCount++;
            }
        });

        const { iq, classification } = calculateIQ(correctCount, iqQuestions.length);

        saveTestResult(correctCount, iqQuestions.length, iq, classification);

        navigate("/quiz/iq-result", {
            state: {
                score: correctCount,
                total: iqQuestions.length,
                correct: correctCount,
                wrong: iqQuestions.length - correctCount,
                iq: iq,                           // ✅ single source of truth, used everywhere
                classification: classification,    // ✅ e.g. "Above Average", "Superior", etc.
                userAnswers: answers,
                questions: iqQuestions
            },
        });
    };

    const currentQuestion = iqQuestions[currentQuestionIndex];
    const progressPercentage =
        ((currentQuestionIndex + 1) / iqQuestions.length) * 100;

    const theme = {
        bgMain: "#0a140f",
        bgCard: "#111c17",
        accent: "#28a745",
        textMain: "#ffffff",
        textMuted: "#b4bebd",
        border: "#233127",
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                background: theme.bgMain,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "2rem",
                color: theme.textMain,
            }}
        >
            {/* 🔝 TOP BAR (NO TIMER NOW) */}
            <div
                style={{
                    width: "100%",
                    maxWidth: "1000px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "2rem",
                }}
            >
                <div style={{ fontWeight: "600", color: theme.textMuted }}>
                    Question {currentQuestionIndex + 1} of {iqQuestions.length}
                </div>

                {/* 📊 PROGRESS */}
                <div
                    style={{
                        flex: 1,
                        height: "10px",
                        background: "#233127",
                        borderRadius: "5px",
                        margin: "0 2rem",
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{
                            width: `${progressPercentage}%`,
                            height: "100%",
                            background: theme.accent,
                        }}
                    ></div>
                </div>
            </div>

            {/* 📦 QUESTION CARD */}
            <div
                style={{
                    background: theme.bgCard,
                    width: "100%",
                    maxWidth: "1000px",
                    borderRadius: "20px",
                    padding: "3rem",
                    border: `1px solid ${theme.border}`,
                }}
            >
                <h2 style={{ textAlign: "center", marginBottom: "1rem" }}>
                    {currentQuestionIndex + 1}. {currentQuestion.question}
                </h2>

                {/* IMAGE */}
                {currentQuestion.image && (
                    <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                        <img
                            src={currentQuestion.image}
                            alt="Question"
                            style={{
                                maxWidth: "500px",
                                width: "100%",
                                borderRadius: "12px",
                            }}
                        />
                    </div>
                )}

                {/* OPTIONS */}
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {currentQuestion.options.map((option, idx) => {
                        const isSelected =
                            answers[currentQuestionIndex] === option;

                        return (
                            <div
                                key={idx}
                                onClick={() => handleOptionSelect(option)}
                                style={{
                                    border: `2px solid ${isSelected ? theme.accent : theme.border}`,
                                    borderRadius: "10px",
                                    padding: "1rem",
                                    cursor: "pointer",
                                    background: isSelected ? theme.accent : "transparent",
                                    color: isSelected ? "#fff" : theme.textMain,
                                }}
                            >
                                {option}
                            </div>
                        );
                    })}
                </div>

                {/* BUTTONS */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "2rem",
                    }}
                >
               {currentQuestionIndex > 0 && (
    <button onClick={handlePrevious} style={{backgroundColor:"#28a745",color:"#fff",border:"none",padding:"0.8rem 1.8rem",borderRadius:"8px",fontWeight:"600",cursor:"pointer"}}>Back</button>
)}
                  <button
    onClick={handleNext}
    style={{
        backgroundColor: "#28a745",   // ✅ green
        color: "#fff",
        border: "none",
        padding: "0.8rem 1.8rem",
        borderRadius: "8px",
        fontWeight: "600",
        cursor: "pointer",
    }}
>
    {currentQuestionIndex === iqQuestions.length - 1
        ? "Submit"
        : "Next"}
</button>
                </div>
            </div>

            {/* ⏳ TIMER BELOW CARD */}
            <div
                style={{
                    marginTop: "2rem",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        fontWeight: "700",
                        fontSize: "20px",
                        color: "#fff",
                        background: "#000",
                        padding: "10px 20px",
                        borderRadius: "25px",
                        border: "2px solid #28a745",
                    }}
                >
                    <FaClock />
                    <span>{formatTime(timeLeft)}</span>
                </div>
            </div>
        </div>
    );
}
export default IqQuizPage;